import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, ExternalLink, Loader2, Volume2, XCircle } from "lucide-react";
import { api } from "@/auth/AuthContext";

const STATUS_LABEL = {
  "": "Baru masuk", menunggu: "Dilayani", tidak_di_zoom: "Tidak ada di Zoom", selesai: "Selesai",
  pending: "Menunggu verifikasi", approved: "Terverifikasi", rejected: "Ditolak",
};

const StatusTag = ({ guest }) => {
  const noShow = guest.admin_status === "tidak_di_zoom";
  const [alt, setAlt] = useState(false);
  useEffect(() => {
    if (!noShow) return undefined;
    const t = setInterval(() => setAlt((v) => !v), 1400);
    return () => clearInterval(t);
  }, [noShow]);
  if (noShow) {
    return (
      <span className="laksmi-status-tag s-tidak_di_zoom laksmi-status-blink" data-testid={`laksmi-guest-status-${guest.guest_id}`}>
        {alt ? "Cek di Zoom jika Tamu udah ada" : "Tidak ada di Zoom"}
      </span>
    );
  }
  const cls = `s-${guest.is_login ? (guest.admin_status || "new") : guest.verification}`;
  const label = guest.is_login ? STATUS_LABEL[guest.admin_status] : STATUS_LABEL[guest.verification];
  return <span className={`laksmi-status-tag ${cls}`} data-testid={`laksmi-guest-status-${guest.guest_id}`}>{label}</span>;
};

export default function LaksmiGuests() {
  const [guests, setGuests] = useState([]);
  const [menuId, setMenuId] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    const res = await api.get("/laksmi/admin/guests");
    setGuests(res.data.guests || []);
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  const verify = async (id, approve, reason = "") => { setRejectId(null); setRejectReason(""); await api.post(`/laksmi/admin/guests/${id}/verify`, { approve, reason }); await load(); };
  const setStatus = async (id, status) => { setMenuId(null); await api.post(`/laksmi/admin/guests/${id}/status`, { status }); await load(); };

  return (
    <div className="laksmi-page laksmi-guests" data-testid="laksmi-guests-page">
      <div className="laksmi-admin-head">
        <h1>Data Tamu LAKSMI</h1>
        <span className="laksmi-pill open" data-testid="laksmi-sound-on"><Volume2 size={13} /> Suara Notifikasi Aktif</span>
      </div>

      {guests.length === 0 && <div className="laksmi-empty" data-testid="laksmi-guests-empty">Belum ada tamu.</div>}

      <div className="laksmi-guest-list">
        {guests.map((g) => (
          <div key={g.guest_id} className={`laksmi-guest-card ${g.needs_attention ? "attention" : ""}`} data-testid={`laksmi-guest-${g.guest_id}`}>
            <div className="laksmi-guest-main">
              <div className="laksmi-guest-id"><strong>{g.nama}</strong><span>{g.nama_pt}</span></div>
              <div className="laksmi-guest-meta"><em>{g.jabatan}</em><span>Zoom: <b>{g.nama_zoom}</b></span></div>
              <p className="laksmi-guest-topic">{g.topik}</p>
              <StatusTag guest={g} />
            </div>

            <div className="laksmi-guest-actions">
              {!g.is_login && <div className="laksmi-doc-links">
                <a href={g.doc_identitas_link} target="_blank" rel="noreferrer" data-testid={`laksmi-doc-ktp-${g.guest_id}`}><ExternalLink size={12} /> Lihat KTP</a>
                <a href={g.doc_pendukung_link} target="_blank" rel="noreferrer" data-testid={`laksmi-doc-sup-${g.guest_id}`}><ExternalLink size={12} /> Lihat Dokumen</a>
              </div>}

              {!g.is_login && g.verification === "pending"
                ? (rejectId === g.guest_id
                    ? <div className="laksmi-reject-box" data-testid={`laksmi-reject-box-${g.guest_id}`}>
                        <input value={rejectReason} placeholder="Alasan penolakan (mis. KTP tidak terbaca)" data-testid={`laksmi-reject-reason-${g.guest_id}`} onChange={(e) => setRejectReason(e.target.value)} />
                        <div className="laksmi-verify-btns">
                          <button className="laksmi-mini reject" data-testid={`laksmi-reject-confirm-${g.guest_id}`} onClick={() => verify(g.guest_id, false, rejectReason)}>Kirim Penolakan</button>
                          <button className="laksmi-mini" data-testid={`laksmi-reject-cancel-${g.guest_id}`} onClick={() => { setRejectId(null); setRejectReason(""); }}>Batal</button>
                        </div>
                      </div>
                    : <div className="laksmi-verify-btns">
                        <button className="laksmi-mini approve" data-testid={`laksmi-approve-${g.guest_id}`} onClick={() => verify(g.guest_id, true)}><CheckCircle2 size={14} /> Setujui</button>
                        <button className="laksmi-mini reject" data-testid={`laksmi-reject-${g.guest_id}`} onClick={() => { setRejectId(g.guest_id); setRejectReason(""); }}><XCircle size={14} /> Tolak</button>
                      </div>)
                : (g.joined || g.is_login) && <div className="laksmi-status-menu">
                    <button
                      className={`laksmi-status-btn ${g.admin_status ? `set s-${g.admin_status}` : "blink"}`}
                      data-testid={`laksmi-give-status-${g.guest_id}`}
                      onClick={() => setMenuId(menuId === g.guest_id ? null : g.guest_id)}
                    >
                      {g.admin_status ? STATUS_LABEL[g.admin_status] : "Beri Status"}
                    </button>
                    {menuId === g.guest_id && <div className="laksmi-menu-pop" data-testid={`laksmi-status-pop-${g.guest_id}`}>
                      <button data-testid={`laksmi-st-menunggu-${g.guest_id}`} onClick={() => setStatus(g.guest_id, "menunggu")}>Dilayani</button>
                      <button data-testid={`laksmi-st-noshow-${g.guest_id}`} onClick={() => setStatus(g.guest_id, "tidak_di_zoom")}>Tamu tidak ada di Zoom</button>
                      <button data-testid={`laksmi-st-selesai-${g.guest_id}`} onClick={() => setStatus(g.guest_id, "selesai")}>Selesai Dilayani</button>
                    </div>}
                  </div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
